import React, { Component } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  ElementsConsumer,
  PaymentRequestButtonElement,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from '@stripe/react-stripe-js';
import { Box, LinearProgress, Grid } from '@mui/material';
import { DialogActionButton } from 'components/Dialog';
import { useStyles } from './styles';
import withMediaQuery from 'constants/mediaQuery';

// HOC to inject styles into class component
// no-dd-sa:javascript-code-style/parameter-name
const withUseStyles = (WrappedComponent) => {
  return function WithStylesWrapper(props) {
    const classes = useStyles();
    return <WrappedComponent {...props} classes={classes} />;
  };
};

const stripePromise = loadStripe(import.meta.env.REACT_APP_STRIPE_API_KEY);

class StripeForm extends Component {
  // no-dd-sa:javascript-best-practices/no-unreachable
  CARD_OPTIONS = {
    style: {
      base: {
        fontSize: '16px',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  constructor(props) {
    super(props);
    this.state = {
      errorMsg: '',
      canMakePayment: false,
      hasCheckedAvailability: false,
      method: 'card',
    };

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidUpdate() {
    const { hasCheckedAvailability } = this.state;
    const { elements, stripe, tipping, beforeSubmit, onError, onFail, onSuccess } = this.props;

    if (!stripe || !elements || hasCheckedAvailability) return false;

    this.paymentRequest = stripe.paymentRequest({
      country: 'US',
      currency: tipping.currency,
      total: {
        label: 'Tip Jitsu Driver',
        amount: Math.round(tipping.amount.toFixed(2) * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    this.paymentRequest.canMakePayment().then((result) => {
      this.setState({ hasCheckedAvailability: true });
      if (result) this.setState({ canMakePayment: true });
    });

    // handle pay not via credit card
    this.paymentRequest.on('paymentmethod', async (ev) => {
      if (!tipping.tip_client_secret) {
        ev.complete('fail');
        return false;
      }

      if (beforeSubmit) beforeSubmit();
      let result;
      try {
        result = await stripe.confirmCardPayment(tipping.tip_client_secret, { payment_method: ev.paymentMethod.id }, { handleActions: false });
      } catch (e) {
        // request to Stripe rejected (e.g. network) → close the wallet sheet and surface the error
        ev.complete('fail');
        if (onError) onError();
        return;
      }

      if (result.error) {
        // Report to the browser that the payment failed, prompting it to
        // re-show the payment interface, or show an error message and close
        // the payment interface.
        ev.complete('fail');
        this.setState({ errorMsg: result.error.message });
        if (onFail) onFail();
      } else {
        // Report to the browser that the confirmation was successful, prompting
        // it to close the browser payment method collection interface.
        ev.complete('success');
        if (result.paymentIntent.status === 'succeeded') {
          onSuccess?.();
        } else {
          onFail?.();
        }
      }
    });
  }

  handleSubmit(e) {
    e.preventDefault();
    const { elements, stripe, tipping, beforeSubmit, onError, onFail, onSuccess } = this.props;

    if (!stripe || !elements) return false;
    if (!tipping?.tip_client_secret) return false;

    try {
      if (beforeSubmit) beforeSubmit();
      stripe
        .confirmCardPayment(tipping.tip_client_secret, {
          payment_method: {
            card: elements.getElement(CardNumberElement),
          },
        })
        .then((result) => {
          if (result.error) {
            this.setState({ errorMsg: result.error.message });
            if (onFail) onFail();
          } else if (result.paymentIntent.status === 'succeeded') {
            onSuccess?.();
          }
        })
        // request to Stripe rejected (e.g. network) — not reachable by the sync try/catch below
        .catch(() => onError?.());
    } catch (e) {
      if (onError) onError();
    }
  }

  render() {
    const { errorMsg, canMakePayment, hasCheckedAvailability } = this.state;
    const { classes, mediaQuery } = this.props;
    const { isMobile } = mediaQuery;

    if (!hasCheckedAvailability) {
      return (
        <Box>
          <LinearProgress color="primary" />
          <Box py={1} style={{ textAlign: 'center' }}>
            Checking for available payment method...
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <form onSubmit={this.handleSubmit}>
          <Box sx={classes.paymentWrapper}>
            {canMakePayment && (
              <Box py={1} style={{ maxWidth: 300, margin: 'auto' }}>
                <PaymentRequestButtonElement
                  options={{
                    style: {
                      paymentRequestButton: {
                        type: 'default',
                        theme: 'dark',
                      },
                    },
                    paymentRequest: this.paymentRequest,
                  }}
                />
              </Box>
            )}
            <Box>
              <Box py={1.5} sx={classes.boxWrapper}>
                <Box sx={classes.boxDecorator} />
                <Box sx={classes.boxText}>{canMakePayment ? 'OR PAY WITH CARD' : 'CREDIT CARD'}</Box>
              </Box>
              <Grid container spacing={1} style={{ marginBottom: 10 }}>
                <Grid size={12}>
                  <Box mx="auto" p={1} sx={classes.cardElementWrapper}>
                    <CardNumberElement options={this.CARD_OPTIONS} onChange={(e) => this.setState({ errorMsg: '' })} />
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box mx="auto" p={1} sx={classes.cardElementWrapper}>
                    <CardExpiryElement options={this.CARD_OPTIONS} onChange={(e) => this.setState({ errorMsg: '' })} />
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box mx="auto" p={1} sx={classes.cardElementWrapper}>
                    <CardCvcElement options={this.CARD_OPTIONS} onChange={(e) => this.setState({ errorMsg: '' })} />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box my={1} sx={classes.error}>
            {errorMsg}
          </Box>
          <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row', py: 2 }}>
            <DialogActionButton variant={'outlined'} color="primary" sx={classes.button} onClick={() => this.props.onBack()}>
              Back
            </DialogActionButton>
            <DialogActionButton type="submit" variant="contained" color="primary" sx={classes.button}>
              Pay
            </DialogActionButton>
          </Box>
        </form>
      </Box>
    );
  }
}

const InjectedStripeForm = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <ElementsConsumer>{({ elements, stripe }) => <StripeForm elements={elements} stripe={stripe} {...props} />}</ElementsConsumer>
    </Elements>
  );
};

export default withUseStyles(withMediaQuery()(InjectedStripeForm));
